/**
 * A segment definition as ONE LINE of plain words, for the lists index.
 *
 * Pure — no hooks, no fetching. The index renders one of these per row, so a
 * label that needs a request (a stage name, a country name) is NOT resolved
 * here: a `ref` value is shown as a count ("3 values") unless the caller hands
 * in a resolver that already holds the labels. Fetching per row would be one
 * request per list per option source against the shared rate limit.
 *
 * `SEGMENT_OP_LABEL` is the ONE copy of the condition words. RuleEditor reads
 * it too, so the builder and this summary cannot word a condition two ways.
 */
import { formatDateOnly } from '@/lib/format';
import type { SegmentDefinition, SegmentField, SegmentGroup, SegmentOp, SegmentRule } from '@/lib/types';

export const SEGMENT_OP_LABEL: Record<SegmentOp, string> = {
  in: 'is any of',
  not_in: 'is none of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  contains: 'contains',
  not_contains: 'does not contain',
  equals: 'equals',
  before: 'is before',
  after: 'is after',
  within_last_days: 'is within the last (days)',
  is_true: 'is yes',
  is_false: 'is no',
};

export const NO_RULES_SUMMARY = 'No rules (everyone)';

const NO_VALUE: ReadonlySet<SegmentOp> = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);

export interface SummariseOptions {
  /**
   * Resolves a `ref` field's stored value to its label, when the caller
   * already holds the option list. `undefined` means "not known here", and
   * the value is then counted rather than named.
   */
  refLabel?(ref: string, value: string): string | undefined;
}

function isGroup(node: SegmentRule | SegmentGroup): node is SegmentGroup {
  return typeof node === 'object' && node !== null && Array.isArray((node as SegmentGroup).rules);
}

function asStrings(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value === null || value === undefined || value === '') return [];
  return [String(value)];
}

function countWord(n: number): string {
  return n === 1 ? '1 value' : `${n} values`;
}

/** A field's stored values as words: enum labels, resolved ref labels, or a count. */
function optionValues(field: SegmentField, values: string[], options: SummariseOptions): string {
  if (values.length === 0) return 'nothing chosen';
  if (field.type === 'enum') {
    const labels = new Map((field.options ?? []).map((o) => [String(o.value), o.label]));
    return values.map((v) => labels.get(v) ?? v).join(', ');
  }
  const ref = field.ref;
  if (ref && options.refLabel) {
    const resolved = values.map((v) => options.refLabel?.(ref, v));
    if (resolved.every((label): label is string => typeof label === 'string')) return resolved.join(', ');
  }
  return countWord(values.length);
}

function summariseRule(rule: SegmentRule, fields: readonly SegmentField[], options: SummariseOptions): string {
  const field = fields.find((f) => f.key === rule.field) ?? null;
  // An unknown field keeps its key, so a stored rule on a retired field still
  // says something rather than vanishing from the line.
  const label = field ? field.label : rule.field || 'Unknown field';
  const op = rule.op;

  if (NO_VALUE.has(op)) return `${label} ${SEGMENT_OP_LABEL[op] ?? op}`;
  if (op === 'within_last_days') {
    // The builder's label carries "(days)" as a unit hint beside a number box;
    // in a sentence the number takes that place.
    const days = asStrings(rule.value)[0];
    return days ? `${label} is within the last ${days} days` : `${label} ${SEGMENT_OP_LABEL[op]}`;
  }

  const verb = SEGMENT_OP_LABEL[op] ?? op;
  const values = asStrings(rule.value);

  if (field && (field.type === 'enum' || field.type === 'ref')) {
    return `${label} ${verb} ${optionValues(field, values, options)}`;
  }
  if (values.length === 0) return `${label} ${verb} nothing`;
  if (field?.type === 'date') return `${label} ${verb} ${formatDateOnly(values[0] ?? null)}`;
  return `${label} ${verb} "${values.join(', ')}"`;
}

function summariseGroup(
  group: SegmentGroup,
  fields: readonly SegmentField[],
  options: SummariseOptions,
  nested: boolean,
): string {
  const rules = Array.isArray(group.rules) ? group.rules : [];
  if (rules.length === 0) return nested ? '(no rules)' : NO_RULES_SUMMARY;
  const parts = rules.map((node) =>
    isGroup(node) ? summariseGroup(node, fields, options, true) : summariseRule(node, fields, options),
  );
  // Top level "all" reads as a list of conditions; anything nested spells its
  // join out, because a dot inside brackets says nothing about and/or.
  const joiner = group.match === 'any' ? ' or ' : nested ? ' and ' : ' · ';
  const line = parts.join(joiner);
  return nested && parts.length > 1 ? `(${line})` : line;
}

/** One line describing a segment. `null` (a static list) has nothing to describe. */
export function summariseSegment(
  definition: SegmentDefinition | null | undefined,
  fields: readonly SegmentField[],
  options: SummariseOptions = {},
): string {
  if (!definition) return NO_RULES_SUMMARY;
  return summariseGroup(definition, fields, options, false);
}
